import DriverDisplayIdentifierSelect from '@/components/common/driver-display-identifier-select'
import FormSelectInput from '@/components/forms/inputs/form-select-input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form'
import {
  getStandingsEventLabel,
  getStandingsSessionRaceNumber,
} from '@/helpers/event-results'
import { getOrderedChampionshipCarClasses } from '@/lib/championship-car-class-order'
import { cn } from '@/lib/utils'
import { Info } from 'lucide-react'
import { useMemo } from 'react'
import SelectChampionshipSplitsButton from '../select-championship-splits-button'

export default function StandingsFilterForm({
  championship,
  events,
  form,
  selectedEvent,
  selectedEventSessionSequence,
  presetEventId,
  tableType,
  isManagerSide,
  selectedSplits,
  setSelectedSplits,
  driverDisplayIdentifier,
  driverDisplayIdentifierOptions,
  setDriverDisplayIdentifier,
  filterAction,
  disableSplitFilter = false,
}) {
  const eventIdOptions = useMemo(() => {
    return [{ value: null, label: 'Championship Standings' }].concat(
      events.map((e, index) => ({
        value: e.id,
        label: getStandingsEventLabel(e, index, events.length),
      })),
    )
  }, [events])

  // Sort sessions by type: Practice -> Quali -> Race
  const sortedSessions = useMemo(() => {
    if (!selectedEvent) return []

    const sessionOrder = { practice: 1, qualifying: 2, race: 3, hotlap: 4 }

    return [...selectedEvent.sessions].sort((a, b) => {
      return sessionOrder[a.type] - sessionOrder[b.type]
    })
  }, [selectedEvent])

  const showStandingsFilter =
    (!presetEventId || isManagerSide) && !championship.isSingleEvent
  // Only show teams filter if there are teams and it's not a team-only championship
  const showTeamsFilter =
    championship.teams.length > 0 && !championship.isTeamOnly
  const showSplitsFilter = championship.splits.length > 0
  const carClasses = useMemo(
    () => getOrderedChampionshipCarClasses(championship.carClasses),
    [championship.carClasses],
  )
  const showCarClassFilter = tableType === 'driver' && carClasses.length > 1
  const showFilterControls =
    showStandingsFilter ||
    showTeamsFilter ||
    showSplitsFilter ||
    showCarClassFilter
  const showInlineResultsHeader = Boolean(presetEventId && !isManagerSide)
  const selectedEventLabel = selectedEvent
    ? presetEventId && !isManagerSide
      ? 'Results'
      : eventIdOptions.find((e) => e.value === selectedEvent.id)?.label
    : showInlineResultsHeader
      ? 'Results'
      : null

  const displayNameSelector = (
    <DriverDisplayIdentifierSelect
      options={driverDisplayIdentifierOptions}
      value={driverDisplayIdentifier}
      onValueChange={setDriverDisplayIdentifier}
      compact
      inline
      className="ml-auto"
    />
  )
  const headerActions = (
    <div className="ml-auto flex shrink-0 items-center gap-2">
      {displayNameSelector}
      {!showFilterControls ? filterAction : null}
    </div>
  )

  const sessionSelector = selectedEvent ? (
    <FormField
      name="sessionId"
      control={form.control}
      render={({ field }) => (
        <FormItem>
          <FormControl>
            <div className="flex flex-wrap gap-2">
              {sortedSessions.map((session) => {
                const raceCount = getStandingsSessionRaceNumber(
                  selectedEventSessionSequence,
                  session.id,
                )

                return (
                  <Button
                    key={session.id}
                    type="button"
                    className={cn(
                      'flex items-center gap-1 rounded-full border px-4 py-2 capitalize',
                      session.id === field.value
                        ? 'border-shades-0 bg-shades-0 text-neutral-blue-900 hover:bg-neutral-blue-100'
                        : 'border-neutral-blue-600 bg-neutral-blue-700 text-shades-0 hover:bg-neutral-blue-600',
                    )}
                    variant="ghost"
                    onClick={() => field.onChange(session.id)}
                  >
                    <div
                      className={cn(
                        'flex size-6 items-center justify-center rounded-full text-shades-0',
                        session.id === field.value
                          ? 'bg-neutral-blue-800'
                          : 'bg-neutral-blue-600',
                      )}
                    >
                      {session.type[0]}
                    </div>
                    {session.type} {raceCount}
                  </Button>
                )
              })}
            </div>
          </FormControl>
        </FormItem>
      )}
    />
  ) : null
  const sessionToolbar = sessionSelector ? (
    <div className="flex min-w-0 flex-wrap items-end justify-between gap-5">
      <div className="flex min-w-0 flex-col gap-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-neutral-blue-400">
          Sessions
        </span>
        {sessionSelector}
      </div>
      {headerActions}
    </div>
  ) : null

  return (
    <Form {...form}>
      <form
        id="standings-form"
        onSubmit={form.handleSubmit(() => {})}
        className="flex flex-col gap-5"
      >
        {showFilterControls && (
          <div className="flex flex-col gap-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-neutral-blue-400">
              Standings filters
            </span>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="grid min-w-0 flex-1 gap-3 sm:flex sm:flex-wrap">
                {showStandingsFilter && (
                  <FormSelectInput
                    {...form}
                    name="eventId"
                    options={eventIdOptions}
                    placeholder="Championship Standings"
                    btnClassName="h-[52px] rounded-sm border-neutral-blue-700 bg-neutral-blue-800 px-6 text-sm font-semibold text-shades-0 hover:bg-neutral-blue-700"
                  />
                )}

                {showTeamsFilter && (
                  <FormSelectInput
                    {...form}
                    name="tableType"
                    options={[
                      { value: 'driver', label: 'Drivers' },
                      { value: 'team', label: 'Teams' },
                    ]}
                    btnClassName="h-[52px] rounded-sm border-neutral-blue-700 bg-neutral-blue-800 px-6 text-sm font-semibold text-shades-0 hover:bg-neutral-blue-700"
                  />
                )}

                {showCarClassFilter && (
                  <FormSelectInput
                    {...form}
                    name="carClassId"
                    options={[
                      { value: 'all', label: 'All car classes' },
                      ...carClasses.map((carClass) => ({
                        value: carClass.id,
                        label: carClass.name,
                      })),
                    ]}
                    btnClassName="h-[52px] rounded-sm border-neutral-blue-700 bg-neutral-blue-800 px-6 text-sm font-semibold text-shades-0 hover:bg-neutral-blue-700"
                  />
                )}

                {showSplitsFilter && (
                  <SelectChampionshipSplitsButton
                    disabled={disableSplitFilter}
                    selectedSplits={selectedSplits}
                    setSelectedSplits={setSelectedSplits}
                    splits={championship.splits}
                  />
                )}
              </div>
              {filterAction}
            </div>
          </div>
        )}

        <div className={showInlineResultsHeader ? '' : 'mt-10'}>
          {showInlineResultsHeader ? (
            <div className="flex flex-col gap-3">
              <h4 className="text-h-4 font-semibold">{selectedEventLabel}</h4>

              {sessionToolbar}
            </div>
          ) : selectedEvent ? (
            <div className="flex flex-col gap-3">
              <h4 className="text-h-4 font-semibold">{selectedEventLabel}</h4>

              {sessionToolbar}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h4 className="text-h-4 font-semibold capitalize">
                  {tableType ?? 'Driver'}{' '}
                  {championship.isSingleEvent ? 'Event' : 'Championship'}{' '}
                  Standings
                </h4>
                {headerActions}
              </div>

              {championship.dropRoundConfiguration ? (
                <Badge
                  variant="outline"
                  className="w-fit gap-1.5 border-neutral-blue-600 bg-transparent px-3 py-1 font-normal text-neutral-blue-200"
                >
                  <Info className="size-3.5" aria-hidden="true" />
                  Drop rounds apply after the final scheduled race
                </Badge>
              ) : null}
            </div>
          )}
        </div>
      </form>
    </Form>
  )
}
